import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtMoney } from '../utils/formatters';
interface HiringFunnelProps {
  estimatedHires: number;
}
function HiringFunnel({
  estimatedHires
}: HiringFunnelProps) {
  const [editingRate, setEditingRate] = useState<number | null>(null);
  const [tempRate, setTempRate] = useState('');
  // Conversion rates (editable)
  const [rates, setRates] = useState({
    offeredToHired: 85,
    interviewedToOffered: 70,
    scheduledToInterviewed: 80,
    qualifiedToScheduled: 75,
    screenedToQualified: 60,
    approvedToScreened: 50,
    applicationsToApproved: 40
  });
  // Calculate backwards from estimatedHires to get required applications
  const applications = Math.round(estimatedHires / (rates.offeredToHired / 100) / (rates.interviewedToOffered / 100) / (rates.scheduledToInterviewed / 100) / (rates.qualifiedToScheduled / 100) / (rates.screenedToQualified / 100) / (rates.approvedToScreened / 100) / (rates.applicationsToApproved / 100));
  // Calculate forward through the funnel
  const approved = Math.round(applications * (rates.applicationsToApproved / 100));
  const screened = Math.round(approved * (rates.approvedToScreened / 100));
  const qualified = Math.round(screened * (rates.screenedToQualified / 100));
  const scheduled = Math.round(qualified * (rates.qualifiedToScheduled / 100));
  const interviewed = Math.round(scheduled * (rates.scheduledToInterviewed / 100));
  const offered = Math.round(interviewed * (rates.interviewedToOffered / 100));
  const hired = Math.round(offered * (rates.offeredToHired / 100));
  const stages = [{
    label: 'Applications',
    value: applications,
    gradient: 'from-gray-300 to-gray-200',
    rate: rates.applicationsToApproved,
    rateKey: 'applicationsToApproved'
  }, {
    label: 'Approved',
    value: approved,
    gradient: 'from-gray-400 to-gray-300',
    rate: rates.approvedToScreened,
    rateKey: 'approvedToScreened'
  }, {
    label: 'Screened',
    value: screened,
    gradient: 'from-blue-500 to-blue-400',
    rate: rates.screenedToQualified,
    rateKey: 'screenedToQualified'
  }, {
    label: 'Qualified',
    value: qualified,
    gradient: 'from-indigo-400 to-indigo-300',
    rate: rates.qualifiedToScheduled,
    rateKey: 'qualifiedToScheduled'
  }, {
    label: 'Scheduled',
    value: scheduled,
    gradient: 'from-indigo-500 to-indigo-400',
    rate: rates.scheduledToInterviewed,
    rateKey: 'scheduledToInterviewed'
  }, {
    label: 'Interviewed',
    value: interviewed,
    gradient: 'from-purple-400 to-purple-300',
    rate: rates.interviewedToOffered,
    rateKey: 'interviewedToOffered'
  }, {
    label: 'Offered',
    value: offered,
    gradient: 'from-amber-500 to-amber-400',
    rate: rates.offeredToHired,
    rateKey: 'offeredToHired'
  }, {
    label: 'Hired',
    value: hired,
    gradient: 'from-navy to-navy/90',
    rate: null
  }];
  const maxValue = stages[0].value;
  const handleRateClick = (index: number, currentRate: number) => {
    setEditingRate(index);
    setTempRate(currentRate.toString());
  };
  const handleRateSave = (rateKey: string) => {
    const newRate = parseFloat(tempRate);
    if (!isNaN(newRate) && newRate > 0 && newRate <= 100) {
      // Round to nearest 5
      const roundedRate = Math.round(newRate / 5) * 5;
      setRates({
        ...rates,
        [rateKey]: roundedRate
      });
    }
    setEditingRate(null);
    setTempRate('');
  };
  const handleRateCancel = () => {
    setEditingRate(null);
    setTempRate('');
  };
  const handleKeyDown = (e: React.KeyboardEvent, rateKey: string) => {
    if (e.key === 'Enter') {
      handleRateSave(rateKey);
    } else if (e.key === 'Escape') {
      handleRateCancel();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const currentRate = parseFloat(tempRate) || 0;
      const newRate = Math.min(100, currentRate + 5);
      setTempRate(newRate.toString());
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const currentRate = parseFloat(tempRate) || 0;
      const newRate = Math.max(5, currentRate - 5);
      setTempRate(newRate.toString());
    }
  };
  return <div className="space-y-3" data-id="element-718">
      {stages.map((stage, index) => <div key={index} className="flex items-center gap-4" data-id="element-719">
          <div className="w-8 text-right flex-shrink-0" data-id="element-720">
            <span className="text-xl font-bold text-navy" data-id="element-721">{stage.value}</span>
          </div>
          <div className="w-48 flex-shrink-0" data-id="element-722">
            <span className="text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-723">
              {stage.label}
            </span>
          </div>
          {stage.gradient && <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden" data-id="element-724">
              <div className={`h-full bg-gradient-to-r ${stage.gradient} transition-all duration-500 ease-out`} style={{
          width: `${stage.value / maxValue * 100}%`
        }} data-id="element-725" />
            </div>}
          {stage.rate !== null && <div className="w-20 text-right flex-shrink-0" data-id="element-726">
              {editingRate === index ? <input type="number" value={tempRate} onChange={e => setTempRate(e.target.value)} onKeyDown={e => handleKeyDown(e, stage.rateKey as string)} onBlur={() => handleRateSave(stage.rateKey as string)} autoFocus step="5" min="5" max="100" className="w-full text-right bg-transparent border-none outline-none focus:text-amber transition-colors text-sm font-semibold" data-id="element-727" /> : <button onClick={() => handleRateClick(index, stage.rate!)} className="text-sm font-semibold text-gray-600 hover:text-amber transition-colors" data-id="element-728">
                  {stage.rate}%
                </button>}
            </div>}
          {stage.rate === null && <div className="w-20 flex-shrink-0" data-id="element-729" />}
        </div>)}
    </div>;
}
export function RetentionBreakdown() {
  const navigate = useNavigate();
  // Hourly rates for calculating costs
  const [adminHourlyRate, setAdminHourlyRate] = useState(40);
  const [leadershipHourlyRate, setLeadershipHourlyRate] = useState(40);
  // Recruiting assumptions state
  const [adSpend, setAdSpend] = useState(4000);
  const [cph, setCph] = useState(500);
  // Efficiency options
  const efficiencyOptions = [{
    label: 'High Efficiency',
    value: 250
  }, {
    label: 'Medium Efficiency',
    value: 500
  }, {
    label: 'Low Efficiency',
    value: 750
  }, {
    label: 'No Efficiency',
    value: 1000
  }];
  // Get inputs from localStorage
  const storedInputs = localStorage.getItem('calculatorInputs');
  const inputs = storedInputs ? JSON.parse(storedInputs) : {};
  const estimatedHires = Math.round((adSpend || 4000) / (cph || 500));
  const estimatedLaunched = Math.round(estimatedHires * 0.7);
  const estimatedActive30 = estimatedLaunched * 0.3;
  const estimatedActive90 = estimatedActive30 * 0.8;
  const estimatedActive1Year = estimatedActive90 * 0.3;
  // Calculate time and costs
  const preInterviewHours = estimatedHires * 5;
  const onboardingTrainingHours = estimatedLaunched * 5;
  const preInterviewCost = preInterviewHours * adminHourlyRate;
  const onboardingTrainingCost = onboardingTrainingHours * leadershipHourlyRate;
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };
  const handleContinue = () => {
    // Save updated values to localStorage before continuing
    const updatedInputs = {
      ...inputs,
      adSpend,
      cph
    };
    localStorage.setItem('calculatorInputs', JSON.stringify(updatedInputs));
    navigate('/results');
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-730">
      {/* Header */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-731">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6 flex items-center justify-center" data-id="element-732">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12" data-id="element-733" />
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-4 py-8 md:py-12" data-id="element-734">
        {/* Hero */}
        <div className="text-center mb-8 md:mb-12" data-id="element-735">
          <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3 md:mb-4" data-id="element-736">
            Understanding Your Recruiting Funnel
          </h1>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-737">
            Before we show your results, let's break down the metrics that drive
            your ROI
          </p>
        </div>

        {/* Recruiting Assumptions Section */}
        <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8 mb-8" data-id="element-738">
          <div className="border-b-2 border-gray-200 pb-3 mb-6" data-id="element-739">
            <h2 className="text-lg font-bold text-navy" data-id="element-740">
              Recruiting Assumptions
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-741">
              Adjust these values to see how they impact your funnel
            </p>
          </div>

          <div className="space-y-6" data-id="element-742">
            {/* Monthly Ad Spend */}
            <div data-id="element-743">
              <div className="flex items-center justify-between mb-3" data-id="element-744">
                <label className="text-sm font-semibold text-gray-700" data-id="element-745">
                  Monthly Ad Spend (Indeed/Job Boards)
                </label>
                <span className="text-lg font-bold text-navy" data-id="element-746">
                  {fmtMoney(adSpend)}
                </span>
              </div>
              <input type="range" min="500" max="10000" step="500" value={adSpend} onChange={e => setAdSpend(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
              background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(adSpend - 500) / (10000 - 500) * 100}%, #E5E7EB ${(adSpend - 500) / (10000 - 500) * 100}%, #E5E7EB 100%)`
            }} data-id="element-747" />
              <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-748">
                <span data-id="element-749">$500</span>
                <span data-id="element-750">$10,000</span>
              </div>
            </div>

            {/* Cost Per Hire - Button Options */}
            <div data-id="element-751">
              <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-752">
                Ad Spend Cost Per Hire
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-753">
                {efficiencyOptions.map(option => <button key={option.value} onClick={() => setCph(option.value)} className={`px-4 py-3 rounded-lg border-2 font-semibold text-sm transition-all ${cph === option.value ? 'bg-amber border-amber text-navy shadow-lg' : 'bg-white border-gray-200 text-gray-700 hover:border-amber hover:bg-amber/10'}`} data-id="element-754">
                    <div className="text-xs uppercase tracking-wide mb-1" data-id="element-755">
                      {option.label}
                    </div>
                    <div className="text-lg font-bold" data-id="element-756">
                      {fmtMoney(option.value)}
                    </div>
                  </button>)}
              </div>
            </div>

            {/* Estimated Hires Display */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-757">
              <div className="flex items-center justify-between" data-id="element-758">
                <label className="text-sm font-semibold text-gray-700" data-id="element-759">
                  Estimated Monthly Hires
                </label>
                <span className="text-2xl font-bold text-navy" data-id="element-760">
                  {estimatedHires}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-2" data-id="element-761">
                Based on {fmtMoney(adSpend)} ÷ {fmtMoney(cph)} per hire
              </p>
            </div>
          </div>
        </div>

        {/* Main Container */}
        <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8 space-y-8" data-id="element-762">
          {/* Overhead Section */}
          <div className="space-y-4" data-id="element-763">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-764">
              <h2 className="text-lg font-bold text-navy" data-id="element-765">
                Overhead Assumptions
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-766">
              {/* Time Investment */}
              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6" data-id="element-767">
                <h3 className="text-base font-semibold text-gray-700 mb-4" data-id="element-768">
                  Time Investment
                </h3>
                <div className="space-y-4" data-id="element-769">
                  <div data-id="element-770">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-771">
                      Pre-Interview
                    </label>
                    <div className="text-xl font-bold text-navy" data-id="element-772">
                      {preInterviewHours} hours
                    </div>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-773">
                      5 hours × {estimatedHires} hires
                    </p>
                  </div>
                  <div data-id="element-774">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-775">
                      Onboarding & Training
                    </label>
                    <div className="text-xl font-bold text-navy" data-id="element-776">
                      {onboardingTrainingHours} hours
                    </div>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-777">
                      5 hours × {estimatedLaunched} launched reps
                    </p>
                  </div>
                </div>
              </div>

              {/* Hourly Rates & Costs */}
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6" data-id="element-778">
                <h3 className="text-base font-semibold text-gray-700 mb-4" data-id="element-779">
                  Hourly Rates & Costs
                </h3>
                <div className="space-y-4" data-id="element-780">
                  <div data-id="element-781">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-782">
                      Admin Hourly Rate
                    </label>
                    <div className="flex items-center justify-between mb-2" data-id="element-783">
                      <span className="text-lg font-bold text-navy" data-id="element-784">
                        {fmtMoney(adminHourlyRate)}/hr
                      </span>
                    </div>
                    <input type="range" min="20" max="75" step="5" value={adminHourlyRate} onChange={e => setAdminHourlyRate(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
                    background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(adminHourlyRate - 20) / (75 - 20) * 100}%, #E5E7EB ${(adminHourlyRate - 20) / (75 - 20) * 100}%, #E5E7EB 100%)`
                  }} data-id="element-785" />
                    <div className="flex justify-between text-xs text-gray-500 mt-1 mb-2" data-id="element-786">
                      <span data-id="element-787">$20</span>
                      <span data-id="element-788">$75</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-700" data-id="element-789">
                      Cost: {fmtMoney(preInterviewCost)}
                    </div>
                  </div>
                  <div data-id="element-790">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-791">
                      Leadership Hourly Rate
                    </label>
                    <div className="flex items-center justify-between mb-2" data-id="element-792">
                      <span className="text-lg font-bold text-navy" data-id="element-793">
                        {fmtMoney(leadershipHourlyRate)}/hr
                      </span>
                    </div>
                    <input type="range" min="40" max="200" step="5" value={leadershipHourlyRate} onChange={e => setLeadershipHourlyRate(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
                    background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(leadershipHourlyRate - 40) / (200 - 40) * 100}%, #E5E7EB ${(leadershipHourlyRate - 40) / (200 - 40) * 100}%, #E5E7EB 100%)`
                  }} data-id="element-794" />
                    <div className="flex justify-between text-xs text-gray-500 mt-1 mb-2" data-id="element-795">
                      <span data-id="element-796">$40</span>
                      <span data-id="element-797">$200</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-700" data-id="element-798">
                      Cost: {fmtMoney(onboardingTrainingCost)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recruiting Metrics Section */}
          <div className="space-y-4" data-id="element-799">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-800">
              <h2 className="text-lg font-bold text-navy" data-id="element-801">
                Recruiting Metrics
              </h2>
            </div>
            <HiringFunnel estimatedHires={estimatedHires} data-id="element-802" />
          </div>

          {/* Retention Metrics Section */}
          <div className="space-y-4" data-id="element-803">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-804">
              <h2 className="text-lg font-bold text-navy" data-id="element-805">Retention Metrics</h2>
            </div>

            <div className="space-y-3" data-id="element-806">
              {/* Hired */}
              <div className="flex items-center gap-4" data-id="element-807">
                <div className="w-8 text-right flex-shrink-0" data-id="element-808">
                  <span className="text-xl font-bold text-navy" data-id="element-809">
                    {estimatedHires}
                  </span>
                </div>
                <div className="w-48 flex-shrink-0" data-id="element-810">
                  <span className="text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-811">
                    Hired
                  </span>
                </div>
                <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden" data-id="element-812">
                  <div className="h-full bg-gradient-to-r from-navy to-navy/90 w-full transition-all duration-500 ease-out" data-id="element-813" />
                </div>
              </div>

              {/* Launched */}
              <div className="flex items-center gap-4" data-id="element-814">
                <div className="w-8 text-right flex-shrink-0" data-id="element-815">
                  <span className="text-xl font-bold text-navy" data-id="element-816">
                    {estimatedLaunched}
                  </span>
                </div>
                <div className="w-48 flex-shrink-0" data-id="element-817">
                  <span className="text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-818">
                    Launched
                  </span>
                </div>
                <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden" data-id="element-819">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500 ease-out" style={{
                  width: `${estimatedLaunched / estimatedHires * 100}%`
                }} data-id="element-820" />
                </div>
                <div className="w-20 text-right flex-shrink-0" data-id="element-821">
                  <span className="text-sm font-semibold text-gray-600" data-id="element-822">
                    70%
                  </span>
                </div>
              </div>

              {/* Active 30d */}
              <div className="flex items-center gap-4" data-id="element-823">
                <div className="w-8 text-right flex-shrink-0" data-id="element-824">
                  <span className="text-xl font-bold text-navy" data-id="element-825">
                    {Math.round(estimatedActive30)}
                  </span>
                </div>
                <div className="w-48 flex-shrink-0" data-id="element-826">
                  <span className="text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-827">
                    Active 30d
                  </span>
                </div>
                <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden" data-id="element-828">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-500 ease-out" style={{
                  width: `${estimatedActive30 / estimatedHires * 100}%`
                }} data-id="element-829" />
                </div>
                <div className="w-20 text-right flex-shrink-0" data-id="element-830">
                  <span className="text-sm font-semibold text-gray-600" data-id="element-831">
                    30%
                  </span>
                </div>
              </div>

              {/* Active 90d */}
              <div className="flex items-center gap-4" data-id="element-832">
                <div className="w-8 text-right flex-shrink-0" data-id="element-833">
                  <span className="text-xl font-bold text-navy" data-id="element-834">
                    {Math.round(estimatedActive90)}
                  </span>
                </div>
                <div className="w-48 flex-shrink-0" data-id="element-835">
                  <span className="text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-836">
                    Active 90d
                  </span>
                </div>
                <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden" data-id="element-837">
                  <div className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500 ease-out" style={{
                  width: `${estimatedActive90 / estimatedHires * 100}%`
                }} data-id="element-838" />
                </div>
                <div className="w-20 text-right flex-shrink-0" data-id="element-839">
                  <span className="text-sm font-semibold text-gray-600" data-id="element-840">
                    80%
                  </span>
                </div>
              </div>

              {/* Active 1 Year */}
              <div className="flex items-center gap-4" data-id="element-841">
                <div className="w-8 text-right flex-shrink-0" data-id="element-842">
                  <span className="text-xl font-bold text-navy" data-id="element-843">
                    {Math.round(estimatedActive1Year)}
                  </span>
                </div>
                <div className="w-48 flex-shrink-0" data-id="element-844">
                  <span className="text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-845">
                    Active 1 Year
                  </span>
                </div>
                <div className="flex-1 h-8 bg-gray-100 rounded-full overflow-hidden" data-id="element-846">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-purple-400 transition-all duration-500 ease-out" style={{
                  width: `${estimatedActive1Year / estimatedHires * 100}%`
                }} data-id="element-847" />
                </div>
                <div className="w-20 text-right flex-shrink-0" data-id="element-848">
                  <span className="text-sm font-semibold text-gray-600" data-id="element-849">
                    30%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center" data-id="element-850">
          <button onClick={handleContinue} className="inline-flex items-center gap-3 px-8 py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-lg rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-851">
            <span data-id="element-852">Continue to Full Results</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-853">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-854" />
            </svg>
          </button>
        </div>
      </div>
    </div>;
}